import type { SearchFormState } from "../types";
import { supportsPaperSearchFilters } from "../utils/buildCommand";
import { Examples } from "./Examples";
import { AdvancedField, AdvancedSection, FlagCheckbox } from "./AdvancedSection";
import { FIELD, HELP, LABEL } from "./styles";

/** How many advanced search flags differ from their defaults. */
const countActiveAdvanced = (form: SearchFormState): number =>
  [
    form.since.trim() !== "",
    form.yearMin.trim() !== "",
    form.yearMax.trim() !== "",
    form.exact,
    form.titleOnly,
    form.hasFullText,
    form.boolMode,
    form.journal.trim() !== "",
    form.hasSection.trim() !== "",
    form.hasBlockType.trim() !== "",
    form.excludeJournal.trim() !== "",
    form.excludeYear.trim() !== "",
  ].filter(Boolean).length;

const SOURCE_GROUPS: Array<{
  label: string;
  options: Array<{ value: string; label: string }>;
}> = [
  {
    label: "Papers",
    options: [
      { value: "papers", label: "All papers (11M+)" },
      { value: "pmc", label: "PubMed Central" },
      { value: "biorxiv", label: "bioRxiv" },
      { value: "medrxiv", label: "medRxiv" },
      { value: "arxiv", label: "arXiv" },
      { value: "abstracts", label: "Abstracts (150M+)" },
    ],
  },
  {
    label: "Regulatory & clinical",
    options: [
      { value: "fda", label: "FDA / regulatory (225K+)" },
      { value: "trials", label: "Clinical trials (1M+)" },
    ],
  },
  {
    label: "Databases",
    options: [
      { value: "proteins", label: "Proteins (UniProt)" },
    ],
  },
];

const EXAMPLES = [
  "GLP-1 receptor agonists cardiovascular outcomes",
  "CRISPR delivery lipid nanoparticles",
  "long COVID cognitive impairment",
];

/** Sources whose searches map to per-example queries better than the defaults. */
const SOURCE_EXAMPLES: Record<string, string[]> = {
  fda: ["pembrolizumab", "semaglutide label", "CAR-T safety"],
  trials: ["breast cancer HER2", "GLP-1 obesity phase 3", "gene therapy hemophilia"],
  proteins: ["P03956", "glucagon-like peptide-1 receptor", "EGFR"],
};

/** Canonical content-block vocabulary across the paper corpora. `abstract`
 * intentionally is not listed: it is a section name, not a block type. */
const BLOCK_TYPE_OPTIONS = [
  ["paragraph", "Paragraph"],
  ["table", "Table"],
  ["figure", "Figure"],
  ["list_item", "List item"],
  ["section_header", "Section header"],
  ["title", "Title"],
  ["authors", "Authors"],
  ["affiliation", "Affiliation"],
  ["reference", "Reference"],
  ["caption", "Caption"],
  ["formula", "Formula"],
  ["metadata", "Metadata"],
  ["category", "Category"],
  ["supplement", "Supplement"],
  ["date", "Date"],
  ["correspondence", "Correspondence"],
  ["competing_interests", "Competing interests"],
] as const;

const changeSource = (
  form: SearchFormState,
  source: string,
): SearchFormState => {
  const next = {
    ...form,
    source,
    // Country only applies to trials; clear it when leaving that source.
    trialsCountry: source === "trials" ? form.trialsCountry : "",
  };
  if (supportsPaperSearchFilters(source)) return next;
  return {
    ...next,
    year: "",
    sort: "relevance",
    since: "",
    yearMin: "",
    yearMax: "",
    exact: false,
    titleOnly: false,
    author: false,
    hasFullText: false,
    boolMode: false,
    journal: "",
    hasSection: "",
    hasBlockType: "",
    excludeJournal: "",
    excludeYear: "",
  };
};

const selectClass = `${FIELD} mt-1`;

export const SearchControls = ({
  form,
  onChange,
  onSubmit,
}: {
  form: SearchFormState;
  onChange: (next: SearchFormState) => void;
  onSubmit: () => void;
}) => (
  <div className="space-y-5">
    <div>
      <label
        htmlFor="playground-search-query"
        className={LABEL}
      >
        Query
      </label>
      <input
        className={`${FIELD} mt-1`}
        id="playground-search-query"
        value={form.query}
        onChange={(e) => onChange({ ...form, query: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === "Enter") onSubmit();
        }}
      />
    </div>

    <Examples
      items={(SOURCE_EXAMPLES[form.source] ?? EXAMPLES).map((value) => ({ value }))}
      onPick={(query) => onChange({ ...form, query })}
    />

    <div className="grid grid-cols-2 gap-x-8 gap-y-4">
      <div>
        <label
          htmlFor="playground-search-source"
          className={LABEL}
        >
          Source
        </label>
        <select
          id="playground-search-source"
          className={selectClass}
          value={form.source}
          onChange={(e) => {
            const source = e.target.value;
            onChange(changeSource(form, source));
          }}
        >
          {SOURCE_GROUPS.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.options.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      <div>
        <label
          htmlFor="playground-search-limit"
          className={LABEL}
        >
          Results
        </label>
        <select
          id="playground-search-limit"
          className={selectClass}
          value={form.limit}
          onChange={(e) =>
            onChange({ ...form, limit: parseInt(e.target.value, 10) })
          }
        >
          {[5, 10, 20, 30, 50].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>
      {form.source === "trials" && (
        <div>
          <label
            htmlFor="playground-search-country"
            className={LABEL}
          >
            Country
          </label>
          <select
            id="playground-search-country"
            className={selectClass}
            value={form.trialsCountry}
            onChange={(e) =>
              onChange({ ...form, trialsCountry: e.target.value })
            }
          >
            <option value="">All registries</option>
            <option value="us">United States (ClinicalTrials.gov)</option>
            <option value="eu">Europe (EudraCT / CTIS / ISRCTN)</option>
            <option value="jp">Japan (jRCT / UMIN)</option>
            <option value="cn">China (ChiCTR)</option>
            <option value="intl">International (WHO ICTRP)</option>
          </select>
        </div>
      )}
      {supportsPaperSearchFilters(form.source) && (
        <>
          <div>
            <label
              htmlFor="playground-search-year"
              className={LABEL}
            >
              Year
            </label>
            <input
              id="playground-search-year"
              className={`${FIELD} mt-1`}
              value={form.year}
              placeholder="Any"
              inputMode="numeric"
              onChange={(e) => onChange({ ...form, year: e.target.value })}
            />
          </div>
          <div>
            <label
              htmlFor="playground-search-sort"
              className={LABEL}
            >
              Sort
            </label>
            <select
              id="playground-search-sort"
              className={selectClass}
              value={form.sort}
              onChange={(e) =>
                onChange({ ...form, sort: e.target.value as "relevance" | "date" })
              }
            >
              <option value="relevance">Relevance</option>
              <option value="date">Newest first</option>
            </select>
          </div>
        </>
      )}
    </div>

    {form.source === "proteins" && (
      <p className={HELP}>
        Search UniProt by gene, protein name, or function; the profile then
        joins PDB structures and ChEMBL drugs on the accession.
      </p>
    )}

    {supportsPaperSearchFilters(form.source) && (
      <AdvancedSection
        title="Advanced options"
        activeCount={countActiveAdvanced(form)}
      >
        <div className="grid grid-cols-2 gap-x-8 gap-y-4">
          <AdvancedField label="Recency" flag="--since" htmlFor="pg-adv-since">
            <select
              id="pg-adv-since"
              className={selectClass}
              value={form.since}
              onChange={(e) => onChange({ ...form, since: e.target.value })}
            >
              <option value="">Any time</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="6m">Last 6 months</option>
              <option value="1y">Last year</option>
            </select>
          </AdvancedField>
          <AdvancedField label="Journal (PMC)" flag="--journal" htmlFor="pg-adv-journal">
            <input
              id="pg-adv-journal"
              className={`${FIELD} mt-1`}
              value={form.journal}
              placeholder="e.g. Nature"
              onChange={(e) => onChange({ ...form, journal: e.target.value })}
            />
          </AdvancedField>
          <AdvancedField label="Year from" flag="--year-min" htmlFor="pg-adv-ymin">
            <input
              id="pg-adv-ymin"
              className={`${FIELD} mt-1`}
              value={form.yearMin}
              placeholder="e.g. 2020"
              inputMode="numeric"
              onChange={(e) => onChange({ ...form, yearMin: e.target.value })}
            />
          </AdvancedField>
          <AdvancedField label="Year to" flag="--year-max" htmlFor="pg-adv-ymax">
            <input
              id="pg-adv-ymax"
              className={`${FIELD} mt-1`}
              value={form.yearMax}
              placeholder="e.g. 2024"
              inputMode="numeric"
              onChange={(e) => onChange({ ...form, yearMax: e.target.value })}
            />
          </AdvancedField>
        </div>

        <div className="space-y-1.5">
          <FlagCheckbox
            label="Exact phrase"
            flag="-e"
            checked={form.exact}
            onChange={(v) =>
              onChange({ ...form, exact: v, boolMode: v ? false : form.boolMode })
            }
          />
          <FlagCheckbox
            label="Title only"
            flag="-t"
            checked={form.titleOnly}
            onChange={(v) =>
              onChange({
                ...form,
                titleOnly: v,
                author: v ? false : form.author,
                boolMode: v ? false : form.boolMode,
              })
            }
          />
          <FlagCheckbox
            label="Require indexed full text"
            flag="--has-full-text"
            checked={form.hasFullText}
            onChange={(v) => onChange({ ...form, hasFullText: v })}
          />
          <FlagCheckbox
            label="Boolean expression (AND / OR / NOT)"
            flag="--bool"
            checked={form.boolMode}
            onChange={(v) =>
              onChange({
                ...form,
                boolMode: v,
                exact: v ? false : form.exact,
                titleOnly: v ? false : form.titleOnly,
                author: v ? false : form.author,
              })
            }
          />
          {form.boolMode && (
            <p className={HELP}>
              Boolean mode matches keywords only — write the query like{" "}
              <code className="font-mono">"CRISPR" AND NOT "review"</code>.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-x-8 gap-y-4">
          <AdvancedField
            label="Require section"
            flag="--has-section"
            htmlFor="pg-adv-hassec"
          >
            <input
              id="pg-adv-hassec"
              className={`${FIELD} mt-1`}
              value={form.hasSection}
              placeholder="e.g. Methods"
              onChange={(e) => onChange({ ...form, hasSection: e.target.value })}
            />
          </AdvancedField>
          <AdvancedField
            label="Require block type"
            flag="--has-block-type"
            htmlFor="pg-adv-hasblock"
          >
            <select
              id="pg-adv-hasblock"
              className={selectClass}
              value={form.hasBlockType}
              onChange={(e) =>
                onChange({ ...form, hasBlockType: e.target.value })
              }
            >
              <option value="">Any block type</option>
              {BLOCK_TYPE_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <p className={HELP}>
              Requires at least one block of this type anywhere in the paper.
            </p>
          </AdvancedField>
          <AdvancedField
            label="Exclude journal"
            flag="--exclude-journal"
            htmlFor="pg-adv-exjournal"
          >
            <input
              id="pg-adv-exjournal"
              className={`${FIELD} mt-1`}
              value={form.excludeJournal}
              placeholder="e.g. Cureus"
              onChange={(e) =>
                onChange({ ...form, excludeJournal: e.target.value })
              }
            />
          </AdvancedField>
          <AdvancedField
            label="Exclude year"
            flag="--exclude-year"
            htmlFor="pg-adv-exyear"
          >
            <input
              id="pg-adv-exyear"
              className={`${FIELD} mt-1`}
              value={form.excludeYear}
              placeholder="e.g. 2020"
              inputMode="numeric"
              onChange={(e) => onChange({ ...form, excludeYear: e.target.value })}
            />
          </AdvancedField>
        </div>
      </AdvancedSection>
    )}
  </div>
);
